/* eslint-disable import/extensions */
/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Select, DatePicker, Button, Tooltip, Tag } from "antd";
import type { ColumnsType } from "antd/lib/table";
import type { DatePickerProps } from "antd";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import moment from "moment";
import { DownloadOutlined } from "@ant-design/icons";
import { RangePickerProps } from "antd/lib/date-picker";
import { FileOutlined, FileTextOutlined } from "@ant-design/icons/lib/icons";
import { Filters, FiltersCard } from "../../../components/Filters";
import { Label } from "../../../components/ui";

import api from "../../../services/api";

import { useToast } from "../../../hooks/toast";
// eslint-disable-next-line import/extensions
import Table from "../../../components/Table";
import UserAvatar from "../../../components/UserAvatar";
import { ICustomer } from "../../Customers";
import { IUser } from "../../Users";
import ProjectTypes from "../../../enums/ProjectTypes";
import * as Interface from "../../Project";
import TimeSheetAllocationModal from "./TimeSheetAllocationModal";

interface IRequirement extends Interface.IRequirement {
  status_contract:
    | "CONCLUDED"
    | "DATE_NOT_DEFINED"
    | "DELAYED"
    | "IN_PROGRESS"
    | "FINISHING_TODAY";
}

export interface IProject {
  id: string;
  identifier: string;
  short_description: string;
  full_description: string;
  contractor_name: string;
  contractor_email: string;
  contractor_phone_number: string;
  status: string;
  purchase_order: string;
  customer: ICustomer;
  manager: IUser;
  projectType: IProjectType;
}

export interface IProjectType {
  id: string;
  name: string;
  acronym: ProjectTypes;
}

const AllAllocationsActivities: React.FC = () => {
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [allocationMonths, setAllocationMonths] = useState<any[]>([]);
  const [showAllocationFilters, setShowAllocationFilters] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userFilter, setUserFilter] = useState<string | undefined>(undefined);
  const [users, setUsers] = useState<IUser[]>([]);
  const [modalVisibleSheet, setModalVisibleSheet] = useState<boolean>(false);
  const [selectedActivity, setSelectedActivity] = useState<IRequirement>();
  const [type, setType] = useState<"number_nf" | "number_fiche_service">(
    "number_nf",
  );

  const { addToast } = useToast();
  const { t } = useTranslation(["activities", "projects"]);

  // Filters
  const [monthFilter, setMonthFilter] = useState<number | undefined>();
  const [yearFilter, setYearFilter] = useState<number | undefined>();
  const [month, setMonth] = useState(moment());
  const [expectedStart, setExpectedStart] = useState<string | undefined>();
  const [expectedEnd, setExpectedEnd] = useState<string | undefined>(undefined);

  const allocationSearchParams = useMemo(() => {
    return {
      month: monthFilter,
      year: yearFilter,
      consultant_id: userFilter,
      expected_end: expectedEnd,
      expected_start: expectedStart,
    };
  }, [monthFilter, userFilter, yearFilter, expectedEnd, expectedStart]);

  const handleOpenSheetModal = (
    type: "number_nf" | "number_fiche_service",
    activity?: IRequirement,
  ) => {
    setType(type);
    setSelectedActivity(activity);
    setModalVisibleSheet(true);
  };

  const handleCloseModal = useCallback(() => {
    setModalVisibleSheet(false);
  }, []);

  useEffect(() => {
    setLoadingAllocations(true);
    api
      .get<IRequirement[]>("/activities/allocations", {
        params: allocationSearchParams,
      })
      .then(response => {
        setAllocationMonths(response.data);
        /* const promises: any[] = [];
        response.data.forEach((activity) => {
          const status = new Date() < new Date(activity.year, activity.month);
          if (!status && !activity?.report_fiche_service) {
            console.log(activity);
            const message = `Informe a Folha de Serviço para ${activity?.consultant?.name} no projeto "${activity?.project?.short_description}"`;
            promises.push(
              api.post(
                `/activities/${activity.project.id}/notification/${activity?.consultant_id}`,
                { status: true, message, activity_id: activity.id }
              )
            );
          }
        });
        await Promise.all(promises); */
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os seus meses de alocação, tente novamente.",
        });
      })
      .finally(() => setLoadingAllocations(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocationSearchParams, modalVisibleSheet]);

  useEffect(() => {
    setLoadingUsers(true);
    api
      .get<IUser[]>("/users")
      .then(response => {
        setUsers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os consultores, tente novamente.",
        });
      })
      .finally(() => setLoadingUsers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usersOptions = useMemo(() => {
    if (!users) {
      return undefined;
    }

    return users.map(user => ({
      label: user.name,
      value: user.id,
    }));
  }, [users]);

  const handleExportReport = useCallback((activity: IRequirement) => {
    api
      .get(`/activities/${activity.id}/schedule-file`, {
        responseType: "arraybuffer",
      })
      .then(response => {
        const blob = new Blob([response.data], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `${activity.consultant?.name}_${activity.month + 1}_${
          activity.year
        }_timesheet.xlsx`;
        link.click();
      });
  }, []);

  const sorterByFirstLetter = (
    a: string | undefined,
    b: string | undefined,
  ): number => {
    const nameA = a;
    const nameB = b;
    if (nameA && nameB) {
      return nameA.localeCompare(nameB);
    }
    if (nameA && !nameB) return -1;
    if (!nameA && nameB) return 1;
    return 0;
  };

  const sorterDate = (a: IRequirement, b: IRequirement): number => {
    const expected_startA = a.project.expected_start;
    const expected_startB = b.project.expected_start;
    const dateA = new Date(expected_startA).getTime();

    const dateB = new Date(expected_startB).getTime();

    return dateA - dateB;
    /* if (dateA !== null && dateB !== null) {
      return dateA - dateB;
    }
    return -1; */
  };

  const allocationMonthsColumns: ColumnsType<IRequirement> = [
    {
      title: t("project"),
      dataIndex: "project",
      render: (project: IProject) => {
        return (
          <>
            <Link to={`/app/projects/${project.id}`}>
              {project.short_description}
            </Link>
          </>
        );
      },
      sorter: (a, b) =>
        sorterByFirstLetter(
          a.project.short_description,
          b.project.short_description,
        ),
    },
    {
      title: t("allocation_month"),
      dataIndex: "month",
      render: (month, record: IRequirement) => {
        const date = new Date(record.year, record.month);
        return moment(date).format("MMMM [de] YYYY");
      },
      sorter: (a, b) => {
        const dateA = new Date(a.year, a.month - 1); // Mês começa do zero em JavaScript
        const dateB = new Date(b.year, b.month - 1); // Mês começa do zero em JavaScript
        return dateA.getTime() - dateB.getTime();
      },
      defaultSortOrder: "ascend",
    },
    {
      title: t("hours"),
      dataIndex: "hours",
      render: (hours: number) => hours && `${hours} h`,
    },
    {
      title: t("consultant"),
      dataIndex: "consultant",
      render: (consultant: IUser) => {
        if (consultant) {
          return (
            <UserAvatar
              name={consultant.name}
              url={consultant.avatar_url}
              size={36}
            />
          );
        }
        return <></>;
      },
      sorter: (a, b) =>
        sorterByFirstLetter(a.consultant?.name, b.consultant?.name),
    },
    {
      title: t("Data de Início"),
      dataIndex: "expected_start",
      render(_: string, { project }: IRequirement): string {
        if (!project?.expected_start) return "Data não informada";
        return moment(project.expected_start).format("DD/MM/YYYY");
      },
      sorter: (a, b) => sorterDate(a, b),
    },
    {
      title: t("Data de Fim"),
      dataIndex: "expected_end",
      render(_: string, { project }: IRequirement): string {
        if (!project?.expected_end) return "Data não informada";
        return moment(project.expected_end).format("DD/MM/YYYY");
      },
      sorter: (a, b) => sorterDate(a, b),
    },
    {
      title: t("status_contract.label", { ns: "projects" }),
      dataIndex: "status_contract",
      render: (_: string, record: IRequirement) => {
        return (
          <Tag
            color={t(
              `status_contract.options.${record.status_contract}.color`,
              { ns: "projects" },
            )}
            key={record.status_contract}
          >
            {t(`status_contract.options.${record.status_contract}.label`, {
              ns: "projects",
            })}
          </Tag>
        );
      },
    },
    {
      title: t("status.completed_label", { ns: "projects" }),
      dataIndex: "status",
      render: (_: string, record: IRequirement) => {
        return (
          <Tag
            color={t(`status.options.${record.project.status}.color`, {
              ns: "projects",
            })}
            key={record.project.status}
          >
            {t(`status.options.${record.project.status}.label`, {
              ns: "projects",
            })}
          </Tag>
        );
      },
      sorter: (a, b) =>
        sorterByFirstLetter(
          t(`status.options.${a.project.status}.label`, {
            ns: "projects",
          }),
          t(`status.options.${b.project.status}.label`, {
            ns: "projects",
          }),
        ),
    },
    {
      title: t("actions.label"),
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => (
        <>
          <Tooltip title="Exportar planilha">
            <Button
              onClick={() => handleExportReport(record)}
              type="link"
              disabled={new Date() < new Date(record.year, record.month)}
              icon={<DownloadOutlined />}
            />
          </Tooltip>
          <Tooltip title="Informar Folha de Serviço">
            <Button
              onClick={
                () => handleOpenSheetModal("number_fiche_service", record)
                // ts-ignore
              }
              type="link"
              disabled={new Date() < new Date(record.year, record.month)}
              icon={<FileOutlined />}
            />
          </Tooltip>
          {record.number_fiche_service && (
            <Tooltip title="Informar Número NFS">
              <Button
                onClick={() => handleOpenSheetModal("number_nf", record)}
                type="link"
                icon={<FileTextOutlined />}
              />
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  const handleShowAllocationFilters = useCallback(
    value => {
      setShowAllocationFilters(value);
      // As the 'showFilters' has not changed yet, within this function,
      // its logic is the reverse to clear all filters (false = true)
      if (showAllocationFilters) {
        setMonthFilter(undefined);
        setYearFilter(undefined);
        setUserFilter(undefined);
      }
    },
    [showAllocationFilters],
  );

  const onChangeMonth: DatePickerProps["onChange"] = useCallback((date, _) => {
    if (date) {
      setMonth(date.format("MM-YYYY"));
      setMonthFilter(date.month());
      setYearFilter(date.year());
    } else {
      setMonthFilter(undefined);
      setYearFilter(undefined);
    }
  }, []);

  const onChangeExpectedEnd: DatePickerProps["onChange"] = useCallback(
    (date, _) => {
      if (date) {
        setExpectedEnd(date.format("YYYY-MM-DD"));
      } else {
        setExpectedEnd(undefined);
      }
    },
    [],
  );

  const onChangeExpectedStart: DatePickerProps["onChange"] = useCallback(
    (date, _) => {
      if (date) {
        setExpectedStart(date.format("YYYY-MM-DD"));
      } else {
        setExpectedStart(undefined);
      }
    },
    [],
  );

  const disabledDate: RangePickerProps["disabledDate"] = current => {
    // Can not select days before today and today
    return current && current > moment().endOf("month");
  };

  const handleSelectUser = useCallback(user => setUserFilter(user), []);

  const allocationsFilters = useMemo(
    () =>
      showAllocationFilters ? (
        <FiltersCard>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("allocation_month")}</Label>
            </div>
            <DatePicker
              onChange={onChangeMonth}
              format="MMMM YYYY"
              style={{ width: "100%" }}
              picker="month"
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">
                {t("project_detail.expected_start")}
              </Label>
            </div>
            <DatePicker
              onChange={onChangeExpectedStart}
              format="DD/MM/YYYY"
              style={{ width: "100%" }}
              picker="date"
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("project_detail.expected_end")}</Label>
            </div>
            <DatePicker
              onChange={onChangeExpectedEnd}
              format="DD/MM/YYYY"
              style={{ width: "100%" }}
              picker="date"
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("consultant")}</Label>
            </div>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              onChange={handleSelectUser}
              options={usersOptions}
              placeholder={t("consultant")}
              loading={loadingUsers}
              value={userFilter}
            />
          </Col>
        </FiltersCard>
      ) : (
        <></>
      ),
    [
      handleSelectUser,
      loadingUsers,
      month,
      onChangeMonth,
      showAllocationFilters,
      t,
      userFilter,
      usersOptions,
    ],
  );

  return (
    <>
      <TimeSheetAllocationModal
        activity={selectedActivity}
        visible={modalVisibleSheet}
        handleClose={handleCloseModal}
        type={type}
      />
      <Table
        data={allocationMonths}
        columns={allocationMonthsColumns}
        title={
          <>
            Alocações
            <Filters
              showFilters={showAllocationFilters}
              setShowFilters={handleShowAllocationFilters}
            />
          </>
        }
        footer={() => <></>}
        loading={loadingAllocations}
        pagination={false}
        extraFilters={allocationsFilters}
      />
    </>
  );
};

export default AllAllocationsActivities;
